// src/pages/AdminPanel.tsx
// Panel de Administración con Modal Bootstrap para Sesión Expirada
import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import apiElCristo from '../service/api_elcristo';

// Importar SessionManager y Modal
import SessionManager from '../components/admin/SessionManager';
import SessionExpiredModal from '../components/admin/SessionExpiredModal';

// Importar componentes del admin
import AdminLogin from '../components/admin/AdminLogin';
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminHeader from '../components/admin/sections/AdminHeader';
import AdminHero from '../components/admin/sections/AdminHero';
import AdminAboutUs from '../components/admin/sections/AdminAboutUs';
import AdminActivities from '../components/admin/sections/AdminActivities';
import AdminPrograms from '../components/admin/sections/AdminPrograms';
import AdminTeam from '../components/admin/sections/AdminTeam';
import AdminNews from '../components/admin/sections/AdminNews';
import AdminTestimonials from '../components/admin/sections/AdminTestimonials';
import AdminUsers from '../components/admin/AdminUsers';
import AdminSettings from '../components/admin/AdminSettings';

// CSS del admin
import '../assets/kidsa/admin-elcristo.css';

const AdminPanel: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = apiElCristo.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        try {
          const userData = localStorage.getItem('elcristo_user');
          if (userData) {
            setUser(JSON.parse(userData));
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('elcristo_user');
          apiElCristo.logout();
          setIsAuthenticated(false);
        }
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = async (email: string, password: string) => {
    try {
      const result = await apiElCristo.login(email, password);
      localStorage.setItem('elcristo_user', JSON.stringify(result.user));
      setUser(result.user);
      setIsAuthenticated(true);
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const handleLogout = () => {
    apiElCristo.logout();
    localStorage.removeItem('elcristo_user');
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/';
  };

  // Handler de sesión expirada - Ahora muestra modal Bootstrap
  const handleSessionExpired = () => {
    console.log('🚪 Sesión expirada por inactividad');
    
    // Limpiar localStorage
    localStorage.removeItem('elcristo_token');
    localStorage.removeItem('elcristo_refresh_token');
    localStorage.removeItem('elcristo_user');
    
    // Logout en API
    apiElCristo.logout();
    
    // Actualizar estado
    setIsAuthenticated(false);
    setUser(null);
    
    // Mostrar modal Bootstrap en vez de alert()
    setShowSessionExpiredModal(true);
  };

  // Confirmar en modal y redirigir
  const handleConfirmSessionExpired = () => {
    setShowSessionExpiredModal(false);
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#d5f0ff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="elcristo-spinner"></div>
          <p style={{ marginTop: '20px', color: '#385469', fontWeight: 600 }}>Cargando Panel Administrativo...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="elcristo-admin-panel">
      {/* SessionManager - Monitor de Inactividad */}
      <SessionManager 
        onSessionExpired={handleSessionExpired}
        sessionTimeout={30 * 60 * 1000}
        warningTime={30 * 1000}
      />

      {/* Modal Bootstrap de Sesión Expirada */}
      <SessionExpiredModal 
        show={showSessionExpiredModal}
        onConfirm={handleConfirmSessionExpired}
      />

      {/* Header Fijo */}
      <header className="elcristo-admin-header">
        <div className="elcristo-admin-header-left">
          <button 
            className="elcristo-sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expandir menú' : 'Contraer menú'}
          >
            <i className={`fas fa-${sidebarCollapsed ? 'bars' : 'times'}`}></i>
          </button>
          
          <img 
            src="/assets/kidsa/assets/img/logo/logoheader.png" 
            alt="Logo Colegio El Cristo" 
            className="elcristo-header-logo"
          />
          <div className="elcristo-header-title">
            <h1>Panel de Administración</h1>
            <p>Colegio El Cristo CMS</p>
          </div>
        </div>

        <div className="elcristo-admin-header-right">
          <div className="elcristo-user-info-header">
            <div className="elcristo-user-avatar-small">
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="elcristo-user-details">
              <span className="elcristo-user-name-header">{user?.name || 'Administrador'}</span>
              <span className="elcristo-user-role-header">{user?.role || 'Admin'}</span>
            </div>
          </div>
          
          <button onClick={handleLogout} className="elcristo-btn-logout-header" title="Cerrar sesión">
            <i className="fas fa-sign-out-alt"></i>
            <span>Salir</span>
          </button>
        </div>
      </header>

      {/* Sidebar Mejorado */}
      <aside className={`elcristo-admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <nav className="elcristo-sidebar-nav">
          <Link 
            to="/admin/dashboard" 
            className={`elcristo-nav-link ${location.pathname === '/admin/dashboard' || location.pathname === '/admin' || location.pathname === '/admin/' ? 'active' : ''}`}
          >
            <i className="fas fa-home"></i>
            <span>Dashboard</span>
          </Link>
          
          <div className="elcristo-nav-section">
            <i className="fas fa-edit"></i>
            <span>Contenido del Sitio</span>
          </div>
          
          <Link 
            to="/admin/header" 
            className={`elcristo-nav-link ${location.pathname === '/admin/header' ? 'active' : ''}`}
          >
            <i className="fas fa-bars"></i>
            <span>Menú & Header</span>
          </Link>
          
          <Link 
            to="/admin/hero" 
            className={`elcristo-nav-link ${location.pathname === '/admin/hero' ? 'active' : ''}`}
          >
            <i className="fas fa-image"></i>
            <span>Portada Principal</span>
          </Link>
          
          <Link 
            to="/admin/aboutus" 
            className={`elcristo-nav-link ${location.pathname === '/admin/aboutus' ? 'active' : ''}`}
          >
            <i className="fas fa-info-circle"></i>
            <span>Nosotros</span>
          </Link>
          
          <Link 
            to="/admin/activities" 
            className={`elcristo-nav-link ${location.pathname === '/admin/activities' ? 'active' : ''}`}
          >
            <i className="fas fa-running"></i>
            <span>Actividades</span>
          </Link>
          
          <Link 
            to="/admin/programs" 
            className={`elcristo-nav-link ${location.pathname === '/admin/programs' ? 'active' : ''}`}
          >
            <i className="fas fa-graduation-cap"></i>
            <span>Programas</span>
          </Link>
          
          <Link 
            to="/admin/team" 
            className={`elcristo-nav-link ${location.pathname === '/admin/team' ? 'active' : ''}`}
          >
            <i className="fas fa-chalkboard-teacher"></i>
            <span>Equipo Docente</span>
          </Link>
          
          <Link 
            to="/admin/news" 
            className={`elcristo-nav-link ${location.pathname === '/admin/news' ? 'active' : ''}`}
          >
            <i className="fas fa-newspaper"></i>
            <span>Noticias</span>
          </Link>
          
          <Link 
            to="/admin/comunicados" 
            className={`elcristo-nav-link ${location.pathname === '/admin/comunicados' ? 'active' : ''}`}
          >
            <i className="fas fa-quote-right"></i>
            <span>Comunicados & Anuncios</span>
          </Link>
          
          <div className="elcristo-nav-section">
            <i className="fas fa-cog"></i>
            <span>Administración</span>
          </div>
          
          <Link 
            to="/admin/users" 
            className={`elcristo-nav-link ${location.pathname === '/admin/users' ? 'active' : ''}`}
          >
            <i className="fas fa-user-shield"></i>
            <span>Usuarios</span>
          </Link>

          <Link 
            to="/admin/settings" 
            className={`elcristo-nav-link ${location.pathname === '/admin/settings' ? 'active' : ''}`}
          >
            <i className="fas fa-cog"></i>
            <span>Configuración</span>
          </Link>
        </nav>

        {/* Footer Mejorado del Sidebar */}
        <div className="elcristo-sidebar-footer-new">
          <a 
            href="mailto:soporte@colegioelcristo.cl" 
            className="elcristo-support-button"
            title="Contactar Soporte Técnico"
          >
            <div className="support-icon">
              <i className="fas fa-life-ring"></i>
            </div>
            <div className="support-content">
              <span className="support-title">Soporte Técnico</span>
              <span className="support-subtitle">¿Necesitas ayuda?</span>
            </div>
            <i className="fas fa-chevron-right support-arrow"></i>
          </a>

          <div className="elcristo-sidebar-info">
            <div className="info-item">
              <i className="fas fa-shield-alt"></i>
              <span>Conexión segura</span>
            </div>
            <div className="info-item">
              <i className="fas fa-clock"></i>
              <span>Sesión activa</span>
            </div>
          </div>

          <div className="elcristo-sidebar-version">
            <span>CMS El Cristo v1.0</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="elcristo-admin-main">
        <div className="elcristo-admin-content-wrapper">
          <Routes>
            <Route path="dashboard" element={<AdminDashboard user={user} />} />
            <Route path="header" element={<AdminHeader />} />
            <Route path="hero" element={<AdminHero />} />
            <Route path="aboutus" element={<AdminAboutUs />} />
            <Route path="activities" element={<AdminActivities />} />
            <Route path="programs" element={<AdminPrograms />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="comunicados" element={<AdminTestimonials />} />
            <Route path="users" element={<AdminUsers currentUser={user} />} />
            <Route path="/" element={<AdminDashboard user={user} />} />
            <Route path="settings" element={<AdminSettings />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;